import React from "react";
import { Box } from "@mantine/core";
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import BottomSheet from "../../../common/BottomSheet";
import { ReportMetricDef, ViewSort } from "../../../../types/reportsV2";
import { LABEL_SORT_KEY } from "../../v2/model/viewConfig";
import { sortDirectionLabel, toggleSheetSort } from "../mobileSort";
import SheetOption from "./SheetOption";
import styles from "./MobileSheets.module.css";

export interface MobileSortSheetProps {
  opened: boolean;
  onClose: () => void;
  sort: ViewSort | null;
  /** The label column's name, e.g. "Date / Staff name". */
  labelColumn: string;
  /** Visible columns, in view order. */
  columns: ReportMetricDef[];
  onChange: (sort: ViewSort | null) => void;
}

/**
 * "Sort by": Default, the label column, then each visible column. The
 * sort applies within every level of the tree, as on desktop. The sheet
 * stays open after a pick so a second tap can reverse it.
 */
export default function MobileSortSheet({
  opened,
  onClose,
  sort,
  labelColumn,
  columns,
  onChange,
}: MobileSortSheetProps) {
  const options = [
    { key: LABEL_SORT_KEY, label: labelColumn },
    ...columns.map((c) => ({ key: c.key, label: c.label })),
  ];
  return (
    <BottomSheet
      labelDialog
      opened={opened}
      onClose={onClose}
      title="Sort by"
      data-testid="report-sort-sheet"
    >
      <Box role="radiogroup" aria-label="Sort by" className={styles.options}>
        <SheetOption
          checked={!sort}
          label="Default"
          sub="Newest dates first, then A–Z"
          onClick={() => {
            onChange(null);
            onClose();
          }}
        />
        {options.map((option) => {
          const on = sort?.key === option.key;
          const Arrow = sort?.dir === "asc" ? IconArrowUp : IconArrowDown;
          return (
            <SheetOption
              key={option.key}
              checked={on}
              label={option.label}
              sub={on && sort ? sortDirectionLabel(sort) : undefined}
              trailing={on ? <Arrow size={16} aria-hidden /> : undefined}
              onClick={() => onChange(toggleSheetSort(sort, option.key))}
            />
          );
        })}
      </Box>
    </BottomSheet>
  );
}
